import { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { shouldActivateRow } from '../Plans/rowActivation';
import { missingScoreLabel } from './dimensions';
import type { EvidenceColumnId } from './evidenceColumns';
import {
  flashlightOnly,
  rangeLabel,
  type RosterCohort,
  type RosterDomainKey,
  type RosterRow, planSignOffText } from './rosterRows';
import { StatusChip } from './StatusChip';
import { RowsPerPageSelect, type RowsPerPage } from '../../components/RowsPerPageSelect';
import { ROSTER } from './tokens';

type Props = {
  rows: RosterRow[];
  sortDirection: 'asc' | 'desc';
  onToggleSort: () => void;
  onOpenRow: (id: string) => void;
  page: number;
  totalRows: number;
  rowsPerPage: RowsPerPage;
  onRowsPerPageChange: (rowsPerPage: RowsPerPage) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  evidenceColumns?: readonly EvidenceColumnId[];
  accessUploaded?: boolean;
  showSchool?: boolean;
};

const CELL_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  p: '8px 16px',
  borderBottom: `1px solid ${ROSTER.border}`,
} as const;

const ROW_HOVER_BG = 'rgba(31, 31, 112, 0.04)';
const FOCUS_RING = '#1F1F70';

// Student Name stays put while the rest of the table scrolls sideways under it.
// The divider appears only once something has scrolled underneath.
function pinnedSx(column: RosterColumn, scrolled: boolean) {
  if (column.id !== 'name') return {};
  return {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    bgcolor: ROSTER.cardBg,
    ...(scrolled ? { boxShadow: `inset -1px 0 0 ${ROSTER.border}` } : {}),
  } as const;
}

function cellText(weight: 500 | 600 | 700) {
  return {
    fontFamily: ROSTER.font,
    fontSize: 16,
    fontWeight: weight,
    lineHeight: '24px',
    color: ROSTER.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  } as const;
}

function TextCell({ value, testId }: { value: string; testId?: string }) {
  return (
    <Box component="span" data-testid={testId} sx={cellText(500)}>
      {value}
    </Box>
  );
}

export type RosterColumnId =
  | 'name'
  | 'school'
  | 'teacher'
  | 'grade'
  | 'elStatus'
  | 'cohorts'
  | EvidenceColumnId;

export type RosterColumn = {
  id: RosterColumnId;
  label: string;
  sortable?: boolean;
  sx: Record<string, number>;
  render: (row: RosterRow) => React.ReactNode;
};

export const ROSTER_COLUMNS: readonly RosterColumn[] = [
  {
    id: 'name',
    label: 'Student Name',
    sortable: true,
    sx: { flexGrow: 1, flexBasis: 0 },
    render: (row) => <NameCell row={row} />,
  },
  {
    id: 'school',
    label: 'School',
    sx: { width: ROSTER.columns.school },
    render: (row) => <TextCell value={row.school} />,
  },
  {
    id: 'teacher',
    label: 'Teacher',
    sx: { width: ROSTER.columns.teacher },
    render: (row) => <TeacherCell teachers={row.teachers} />,
  },
  {
    id: 'grade',
    label: 'Grade',
    sx: { width: ROSTER.columns.grade },
    render: (row) => <TextCell value={row.grade} />,
  },
  {
    id: 'elStatus',
    label: 'EL Status',
    sx: { width: ROSTER.columns.elStatus },
    render: (row) => <StatusChip kind={row.elStatus} />,
  },
  {
    id: 'cohorts',
    label: 'Cohorts',
    sx: { width: ROSTER.columns.cohorts },
    render: (row) => <CohortCell cohorts={row.cohorts} />,
  },
];

const NAME_MIN_WIDTH = 200;

const EVIDENCE_LABELS: Record<EvidenceColumnId, string> = {
  yearsInEl: 'Years in EL',
  compositePL: 'Composite PL',
  listening: 'Listening PL',
  speaking: 'Speaking PL',
  reading: 'Reading PL',
  writing: 'Writing PL',
  dataQuality: 'Data quality',
  signOffs: 'Sign-offs',
};

function MissingScoresCell({ domains }: { domains: readonly RosterDomainKey[] }) {
  if (domains.length === 0) return <TextCell value="—" />;
  return (
    <Box
      data-testid="missing-scores"
      sx={{
        ...cellText(500),
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
      }}
    >
      {domains.map((domain) => (
        <Box key={domain} component="span" sx={{ display: 'block' }}>
          {`⚠ ${missingScoreLabel(domain)}`}
        </Box>
      ))}
    </Box>
  );
}

function evidenceValue(row: RosterRow, id: Exclude<EvidenceColumnId, 'dataQuality'>): string {
  if (id === 'signOffs') return planSignOffText(row.planSignOff);
  if (id === 'yearsInEl') return row.yearsInEl == null ? '—' : String(row.yearsInEl);
  const value = id === 'compositePL' ? row.compositePL : row.domainPLs[id];
  return value == null ? '—' : value.toFixed(1);
}

export function evidenceColumn(id: EvidenceColumnId): RosterColumn {
  return {
    id,
    label: EVIDENCE_LABELS[id],
    sx: { width: ROSTER.columns.evidence },
    render: (row: RosterRow) =>
      id === 'dataQuality' ? (
        <MissingScoresCell domains={row.missingDomains} />
      ) : (
        <TextCell value={evidenceValue(row, id)} />
      ),
  };
}

function nameColumn(column: RosterColumn, accessUploaded: boolean): RosterColumn {
  if (column.id !== 'name') return column;
  return { ...column, render: (row) => <NameCell row={row} accessUploaded={accessUploaded} /> };
}

function NameCell({ row, accessUploaded = false }: { row: RosterRow; accessUploaded?: boolean }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <TextCell value={row.name} testId="roster-row-name" />
        {row.enrollment === 'departed' && (
          <Box
            component="span"
            data-testid="roster-row-departed"
            sx={{
              flexShrink: 0,
              p: '2px 8px',
              borderRadius: '999px',
              bgcolor: ROSTER.chip.monitoring.bg,
              color: ROSTER.chip.monitoring.fg,
              fontFamily: ROSTER.font,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: '16px',
            }}
          >
            Departed
          </Box>
        )}
      </Box>
      {flashlightOnly(row, accessUploaded) && (
        <Box
          component="span"
          sx={{
            fontFamily: ROSTER.font,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            color: ROSTER.muted,
          }}
        >
          No ACCESS record
        </Box>
      )}
    </Box>
  );
}

function TeacherCell({ teachers }: { teachers: readonly string[] }) {
  const [first, ...rest] = teachers;
  if (first == null) return <TextCell value="—" />;
  return (
    <Box component="span" sx={cellText(500)}>
      {first}
      {rest.length > 0 && (
        <Tooltip title={rest.join(', ')} describeChild enterTouchDelay={0} leaveTouchDelay={8000}>
          <Box
            component="span"
            sx={{
              display: 'block',
              width: 'fit-content',
              fontFamily: ROSTER.font,
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '16px',
              color: ROSTER.muted,
              marginTop: '2px',
            }}
          >
            {`+${rest.length} more`}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

function CohortCell({ cohorts }: { cohorts: readonly RosterCohort[] }) {
  if (cohorts.length === 0) {
    return (
      <Box component="span" sx={cellText(500)}>
        —
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {cohorts.map((cohort) => (
        <StatusChip key={cohort} kind={cohort} />
      ))}
    </Box>
  );
}

function HeaderCell({
  column,
  sortDirection,
  onToggleSort,
  scrolled,
}: {
  column: RosterColumn;
  sortDirection: 'asc' | 'desc';
  onToggleSort: () => void;
  scrolled: boolean;
}) {
  const sortable = column.sortable === true;
  return (
    <Box
      data-testid="roster-column-header"
      data-column={column.id}
      onClick={sortable ? onToggleSort : undefined}
      sx={{
        ...CELL_SX,
        ...column.sx,
        ...pinnedSx(column, scrolled),
        ...(sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
      }}
    >
      <Box component="span" sx={cellText(700)}>
        {column.label}
      </Box>
      {sortable && (
        <KeyboardArrowDownIcon
          sx={{
            width: 24,
            height: 24,
            color: ROSTER.ink,
            transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
          }}
        />
      )}
    </Box>
  );
}

function DataRow({
  row,
  columns,
  onOpen,
  scrolled,
}: {
  row: RosterRow;
  columns: readonly RosterColumn[];
  onOpen: () => void;
  scrolled: boolean;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={row.enrollment === 'departed' ? `${row.name}, Departed` : row.name}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (shouldActivateRow(e)) {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        display: 'flex',
        cursor: 'pointer',
        '&:hover': { bgcolor: ROW_HOVER_BG },
        // The pinned cell is opaque, so it takes the hover tint and its share of
        // the focus ring itself rather than covering the row's.
        '&:hover > [data-column="name"]': {
          backgroundImage: `linear-gradient(${ROW_HOVER_BG}, ${ROW_HOVER_BG})`,
        },
        '&:focus-visible': { outline: `2px solid ${FOCUS_RING}`, outlineOffset: -2 },
        '&:focus-visible > [data-column="name"]': {
          boxShadow: `inset 2px 2px 0 ${FOCUS_RING}, inset 0 -2px 0 ${FOCUS_RING}`,
        },
      }}
    >
      {columns.map((column) => (
        <Box
          key={column.id}
          data-testid="roster-row-cell"
          data-column={column.id}
          sx={{ ...CELL_SX, ...column.sx, ...pinnedSx(column, scrolled) }}
        >
          {column.render(row)}
        </Box>
      ))}
    </Box>
  );
}

function PagerButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        p: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        borderRadius: '8px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color: ROSTER.ink,
      }}
    >
      {children}
    </Box>
  );
}

export function RosterTable({
  rows,
  sortDirection,
  onToggleSort,
  onOpenRow,
  page,
  totalRows,
  rowsPerPage,
  onRowsPerPageChange,
  onPrevPage,
  onNextPage,
  evidenceColumns = [],
  accessUploaded = false,
  showSchool = true,
}: Props) {
  const [scrolled, setScrolled] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const columns = [
    ...ROSTER_COLUMNS.filter((column) => showSchool || column.id !== 'school').map((column) =>
      nameColumn(column, accessUploaded),
    ),
    ...evidenceColumns.map(evidenceColumn),
  ];
  const gridMinWidth =
    columns.reduce((total, column) => total + (column.sx.width ?? 0), 0) + NAME_MIN_WIDTH;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        data-testid="roster-scroll"
        onScroll={(e: React.UIEvent<HTMLElement>) => setScrolled(e.currentTarget.scrollLeft > 0)}
        sx={{ overflowX: 'auto' }}
      >
        <Box data-testid="roster-grid" sx={{ minWidth: gridMinWidth }}>
          <Box sx={{ display: 'flex' }}>
            {columns.map((column) => (
              <HeaderCell
                key={column.id}
                column={column}
                sortDirection={sortDirection}
                onToggleSort={onToggleSort}
                scrolled={scrolled}
              />
            ))}
          </Box>
          {rows.map((row) => (
            <DataRow
              key={row.id}
              row={row}
              columns={columns}
              onOpen={() => onOpenRow(row.id)}
              scrolled={scrolled}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '26px',
          p: '2px 0px',
        }}
      >
        <RowsPerPageSelect value={rowsPerPage} onChange={onRowsPerPageChange} tokens={ROSTER} />
        <Box
          component="span"
          sx={{
            fontFamily: ROSTER.font,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            color: ROSTER.muted,
          }}
        >
          {rangeLabel(page, rowsPerPage, totalRows)}
        </Box>
        <Box sx={{ display: 'flex' }}>
          <PagerButton label="Previous page" disabled={page <= 1} onClick={onPrevPage}>
            <ChevronLeftIcon sx={{ width: 24, height: 24 }} />
          </PagerButton>
          <PagerButton label="Next page" disabled={page >= totalPages} onClick={onNextPage}>
            <ChevronRightIcon sx={{ width: 24, height: 24 }} />
          </PagerButton>
        </Box>
      </Box>
    </Box>
  );
}
